/**
 * Helpers for the consolidation "version:" activation-date dropdown.
 */
import { toIsoDate } from "@/lib/timelineDates";
import type { DocumentVersionStatus } from "@/models/document/DocumentVersion";

/**
 * True for plain `yyyy-MM-dd` values. Distinguishes activation dates written
 * into `as_of` by the version dropdown from the full ISO timestamps
 * (`approvedAt`) that timeline diamond links carry in the same URL param.
 */
export const isIsoDateOnly = (value: string | null | undefined): boolean =>
  Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value));

/**
 * Formats an ISO `yyyy-MM-dd` date as Danish `dd/mm/yyyy` without going through
 * `Date` (avoids timezone-dependent day shifts for date-only values).
 */
export const formatIsoDateDanish = (isoDate: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return isoDate;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

/**
 * Resolves the dropdown value from priority-ordered candidates: the first
 * candidate that is one of the activation dates wins, otherwise the latest
 * (default) version. Callers put the immediately-known requested date (URL
 * `as_of`) before dates derived from the render result, which lag one fetch
 * behind via React Query placeholder data.
 */
export const resolveSelectedActivationDate = (
  activationDates: string[],
  candidates: ReadonlyArray<string | null | undefined>
): string | undefined => {
  if (activationDates.length === 0) return undefined;
  for (const candidate of candidates) {
    if (candidate && activationDates.includes(candidate)) {
      return candidate;
    }
  }
  return activationDates[activationDates.length - 1];
};

/** The local calendar day as ISO `yyyy-MM-dd`, for comparing against activation dates. */
export const todayIsoDate = (): string => toIsoDate(new Date());

/**
 * Classifies every activation date so the dropdown can say which version is
 * the current one instead of leaving users to infer it from the dates.
 *
 * Activation dates come straight from the consolidated change rows with no
 * cut-off, so a change law adopted now but taking effect next year is already
 * in the list; the version in force is the latest date on or before today, and
 * anything after that is a future version rather than an older one.
 */
export const resolveActivationDateStatuses = (
  activationDates: string[],
  today: string = todayIsoDate()
): Map<string, DocumentVersionStatus> => {
  const inForce = activationDates
    .filter((date) => date <= today)
    .reduce<string | undefined>(
      (latest, date) => (latest === undefined || date > latest ? date : latest),
      undefined
    );

  return new Map(
    activationDates.map((date) => [
      date,
      date > today ? "future" : date === inForce ? "current" : "older",
    ])
  );
};
